/**
 * Sélection des créneaux de collecte et de livraison.
 *
 * Port web des règles de l'app mobile, décrites dans
 * WOSH_MOBILE_V2/mobile-v2/docs/slot-selection-rules.md (état au 23/09/2026).
 * Le serveur ignore la règle des +7 jours et l'appariement journée/soirée :
 * ce sont des règles d'affichage, le client doit les tenir lui-même.
 *
 * Module volontairement pur — pas de React, pas d'appel réseau — pour être
 * testable et partagé entre l'écran collecte et l'écran livraison.
 */

/** Seuil unique dans toute l'app : un créneau est « soirée » à partir de 17h. */
export const EVENING_START_HOUR = 17;

/** Marge minimale avant un créneau de collecte le jour même. */
export const SAME_DAY_LEAD_MINUTES = 60;

/** Décalage d'une collecte de journée vers sa livraison. */
export const DAYTIME_DELIVERY_OFFSET_DAYS = 7;

/**
 * Plancher de livraison après une collecte du soir.
 *
 * Le serveur refuse toute livraison à moins de 48 h du début de la collecte
 * (validate_delivery_date). L'app mobile proposait le lendemain, donc un
 * créneau ensuite rejeté à la confirmation — collecte jeudi 18h, livraison
 * vendredi 18h. Décision du 23/09/2026 : le client s'aligne sur le serveur.
 * "next-day" reste disponible si le serveur est un jour assoupli à 24 h.
 * Voir §7 du document de référence ; le mobile doit appliquer la même règle.
 */
export const EVENING_DELIVERY_FLOOR = "pickup+48h";

const MINUTES_PER_DAY = 24 * 60;

/* ─── Dates ──────────────────────────────────────────────────────────── */

/**
 * Clé de comparaison `YYYY-MM-DD` en heure locale. Toutes les comparaisons de
 * dates passent par là : l'API renvoie tantôt `"2026-09-24"`, tantôt du RFC 822
 * (`"Wed, 23 Sep 2026 00:00:00 +0000"`), et comparer en UTC décalerait d'un jour.
 */
export const dateKey = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : localKey(value);
  }

  const text = String(value).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return text;
  }

  const parsed = new Date(text);

  return Number.isNaN(parsed.getTime()) ? null : localKey(parsed);
};

const localKey = (date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");

/** Décale une clé de date de `days` jours, sans repasser par UTC. */
export const shiftDateKey = (key, days) => {
  if (!key) {
    return null;
  }

  const [year, month, day] = key.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  date.setDate(date.getDate() + days);

  return localKey(date);
};

export const todayKey = (now = new Date()) => localKey(now);

/* ─── Créneaux ───────────────────────────────────────────────────────── */

/**
 * `"08:00-12:00"` → { raw, label, startMinutes, endMinutes }. Renvoie null si
 * la chaîne n'a pas la forme attendue, pour que l'appelant puisse l'écarter.
 */
export const parseSlot = (value) => {
  const raw = typeof value === "string" ? value : value?.time;

  if (typeof raw !== "string") {
    return null;
  }

  const match = raw.match(/^\s*(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})\s*$/);

  if (!match) {
    return null;
  }

  const [, startHour, startMin, endHour, endMin] = match.map(Number);

  return {
    raw: raw.trim(),
    label: `${pad(startHour)}:${pad(startMin)} — ${pad(endHour)}:${pad(endMin)}`,
    startMinutes: startHour * 60 + startMin,
    endMinutes: endHour * 60 + endMin,
  };
};

const pad = (value) => String(value).padStart(2, "0");

export const isEveningSlot = (slot) =>
  Boolean(slot) && slot.startMinutes >= EVENING_START_HOUR * 60;

/* ─── Règles hebdomadaires ───────────────────────────────────────────── */

/**
 * Dates d'une règle. `dates` est la liste complète quand le serveur la fournit ;
 * sinon on retombe sur les trois occurrences historiques, ce qui ne couvre
 * qu'environ trois semaines — trop court dès qu'on applique le +7 jours.
 */
export const datesOf = (rule) => {
  const list = Array.isArray(rule?.dates) && rule.dates.length
    ? rule.dates
    : [rule?.this_date, rule?.next_date, rule?.third_date];

  return list.map(dateKey).filter(Boolean);
};

/* ─── Construction des jours ─────────────────────────────────────────── */

/**
 * @param {Array}  collectionTimes  `collection_times` de l'adresse choisie.
 * @param {string} floorKey         Date la plus tôt acceptée (incluse).
 * @param {Date}   now              Injecté pour rendre la règle des 60 min testable.
 * @param {string} kind             "pickup" applique la marge du jour même.
 * @param {string} allow            "daytime" | "evening" | "all" — les créneaux
 *                                  hors appariement sont marqués `disabled`,
 *                                  pas retirés : le design les affiche grisés.
 * @param {string} disabledBeforeKey / disabledBeforeMinutes
 *                                  Frontière horaire sur un jour pivot, utilisée
 *                                  par le plancher « collecte + 48 h ».
 * @returns {Array} [{ key, day, slots: [{ raw, label, disabled }] }]
 */
export const buildDays = ({
  collectionTimes,
  floorKey,
  now = new Date(),
  kind = "pickup",
  allow = "all",
  disabledBeforeKey = null,
  disabledBeforeMinutes = 0,
}) => {
  const byDate = new Map();
  const today = todayKey(now);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  (collectionTimes || []).forEach((rule) => {
    const slots = (rule?.slots || [])
      .map(parseSlot)
      .filter(Boolean)
      .sort((a, b) => a.startMinutes - b.startMinutes);

    datesOf(rule).forEach((key) => {
      if (floorKey && key < floorKey) {
        return;
      }

      /* Deux règles peuvent tomber le même jour : on fusionne leurs créneaux. */
      const existing = byDate.get(key) || { key, day: rule?.day, slots: [] };

      slots.forEach((slot) => {
        if (
          kind === "pickup" &&
          key === today &&
          slot.startMinutes < nowMinutes + SAME_DAY_LEAD_MINUTES
        ) {
          return;
        }

        if (existing.slots.some((kept) => kept.raw === slot.raw)) {
          return;
        }

        const evening = isEveningSlot(slot);
        const beforeBoundary =
          disabledBeforeKey === key && slot.startMinutes < disabledBeforeMinutes;

        existing.slots.push({
          ...slot,
          disabled:
            beforeBoundary ||
            (allow === "daytime" && evening) ||
            (allow === "evening" && !evening),
        });
      });

      if (existing.slots.length) {
        existing.slots.sort((a, b) => a.startMinutes - b.startMinutes);
        byDate.set(key, existing);
      }
    });
  });

  return [...byDate.values()].sort((a, b) => (a.key < b.key ? -1 : 1));
};

/* ─── Écran collecte ─────────────────────────────────────────────────── */

/** Plancher de l'écran collecte : aujourd'hui. */
export const pickupFloorKey = (now = new Date()) => todayKey(now);

export const buildPickupDays = ({ collectionTimes, now = new Date() }) =>
  buildDays({
    collectionTimes,
    floorKey: pickupFloorKey(now),
    now,
    kind: "pickup",
  });

/**
 * État du cadre d'information permanent, au-dessus du bouton Continuer.
 * Le texte lui-même vit dans les traductions.
 */
export const pickupHint = (slot) => {
  if (!slot) {
    return { kind: "default" };
  }

  return isEveningSlot(slot) ? { kind: "evening" } : { kind: "daytime" };
};

/* ─── Écran livraison ────────────────────────────────────────────────── */

/**
 * Plancher et appariement des créneaux de livraison, à partir de la collecte
 * choisie. `rescheduling` couvre la replanification après incident : la
 * collecte a déjà eu lieu, ni appariement ni +7 jours.
 */
export const deliveryConstraints = ({
  pickupDateKey,
  pickupSlot,
  rescheduling = false,
  eveningFloor = EVENING_DELIVERY_FLOOR,
}) => {
  if (rescheduling) {
    return {
      floorKey: shiftDateKey(pickupDateKey, 1),
      allow: "all",
      disabledBeforeKey: null,
      disabledBeforeMinutes: 0,
    };
  }

  if (!isEveningSlot(pickupSlot)) {
    return {
      floorKey: shiftDateKey(pickupDateKey, DAYTIME_DELIVERY_OFFSET_DAYS),
      allow: "daytime",
      disabledBeforeKey: null,
      disabledBeforeMinutes: 0,
    };
  }

  if (eveningFloor === "pickup+48h") {
    /*
     * « Collecte + 48 h » est un horodatage, pas une date : sur le jour
     * pivot il faut aussi écarter les créneaux qui commencent avant l'heure
     * limite, sinon on reproposerait ce que le serveur refuse.
     */
    const boundaryMinutes = pickupSlot.startMinutes + 2 * MINUTES_PER_DAY;
    const boundaryKey = shiftDateKey(
      pickupDateKey,
      Math.floor(boundaryMinutes / MINUTES_PER_DAY)
    );

    return {
      floorKey: boundaryKey,
      allow: "evening",
      disabledBeforeKey: boundaryKey,
      disabledBeforeMinutes: boundaryMinutes % MINUTES_PER_DAY,
    };
  }

  return {
    floorKey: shiftDateKey(pickupDateKey, 1),
    allow: "evening",
    disabledBeforeKey: null,
    disabledBeforeMinutes: 0,
  };
};

export const buildDeliveryDays = ({
  collectionTimes,
  pickupDateKey,
  pickupSlot,
  rescheduling = false,
  now = new Date(),
  eveningFloor = EVENING_DELIVERY_FLOOR,
}) => {
  const constraints = deliveryConstraints({
    pickupDateKey,
    pickupSlot,
    rescheduling,
    eveningFloor,
  });

  return buildDays({
    collectionTimes,
    now,
    kind: "delivery",
    ...constraints,
  });
};

/** Premier créneau sélectionnable d'un jour — la livraison le présélectionne. */
export const firstAvailableSlot = (day) =>
  (day?.slots || []).find((slot) => !slot.disabled && !slot.full) || null;

/**
 * Restriction à expliquer sous la liste, quand le jour affiché contient au
 * moins un créneau grisé. `firstDaytimeKey` alimente « Première livraison
 * possible : {date} ».
 */
export const deliveryRestriction = (day, allow) => {
  if (!day || allow === "all" || !day.slots?.some((slot) => slot.disabled)) {
    return null;
  }

  return allow === "daytime" ? { kind: "daytime" } : { kind: "evening" };
};

/* ─── Formats API ────────────────────────────────────────────────────── */

/** `"Thursday - 2026-09-24"`, attendu par validate_delivery_date. */
export const apiDate = (day) =>
  day?.day && day?.key ? `${day.day} - ${day.key}` : null;

/** `"08:00-12:00"` : on renvoie la chaîne brute, jamais le libellé affiché. */
export const apiSlot = (slot) => slot?.raw || null;
